import { useState } from "react";
import { db } from "../config/firebase";
import { doc, setDoc, getDoc } from "firebase/firestore";

export interface PublicReportCategoryScores {
  marketOpportunity: number;
  customerUnderstanding: number;
  valueAndProduct: number;
  businessModel: number;
  validationExecution: number;
  [key: string]: number;
}

export interface PublicReportCategoryReasons {
  marketOpportunity?: string;
  customerUnderstanding?: string;
  valueAndProduct?: string;
  businessModel?: string;
  validationExecution?: string;
}

export interface PublicReport {
  slug: string;
  projectTitle: string;
  overallScore: number;
  categoryScores: PublicReportCategoryScores;
  categoryReasons: PublicReportCategoryReasons;
  redFlags: string[];
  strengths: string[];
  recommendations: string[];
  createdAt: string;
}

interface GenerateReportParams {
  projectTitle: string;
  overallScore: number;
  categoryScores: PublicReportCategoryScores;
  categoryReasons: PublicReportCategoryReasons;
  redFlags?: string[];
  strengths?: string[];
  recommendations?: string[];
}

function makeSlug(title: string): string {
  const base = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40);
  const suffix = Math.random().toString(36).slice(2, 8);
  return base ? `${base}-${suffix}` : suffix;
}

export function usePublicReport() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<PublicReport | null>(null);

  const generateReport = async (params: GenerateReportParams): Promise<string | null> => {
    setIsGenerating(true);
    setError(null);
    try {
      const slug = makeSlug(params.projectTitle);
      const reportData: PublicReport = {
        slug,
        projectTitle: params.projectTitle,
        overallScore: Math.round(params.overallScore),
        categoryScores: params.categoryScores,
        categoryReasons: params.categoryReasons,
        redFlags: params.redFlags ?? [],
        strengths: params.strengths ?? [],
        recommendations: params.recommendations ?? [],
        createdAt: new Date().toISOString(),
      };
      await setDoc(doc(db, "reports", slug), reportData);
      return slug;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to generate report link");
      return null;
    } finally {
      setIsGenerating(false);
    }
  };

  const fetchReport = async (slug: string): Promise<void> => {
    setIsFetching(true);
    setError(null);
    try {
      const snap = await getDoc(doc(db, "reports", slug));
      if (!snap.exists()) {
        setError("Report not found");
        return;
      }
      setReport(snap.data() as PublicReport);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load report");
    } finally {
      setIsFetching(false);
    }
  };

  return { generateReport, fetchReport, report, isGenerating, isFetching, error };
}
